import React, { useState, useEffect } from 'react';
import { FileText, Download, Printer, Calendar as CalendarIcon, Mail, CheckSquare } from 'lucide-react';
import EditableExportSheet from './EditableExportSheet';
import FullSheetBuilder from './FullSheetBuilder';
import PurchaseTrackerModal from './PurchaseTrackerModal';
import WallpaperHangList from './WallpaperHangList';
import SheetHeaderImages from './SheetHeaderImages';

const ExportsDashboard = ({ projectId }) => {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [googleCalendarConnected, setGoogleCalendarConnected] = useState(false);
  const [googleCalendarEmail, setGoogleCalendarEmail] = useState('');
  const [teamsCalendarConnected, setTeamsCalendarConnected] = useState(false);
  const [showGoogleSheetsImport, setShowGoogleSheetsImport] = useState(false);
  const [googleSheetsUrl, setGoogleSheetsUrl] = useState('');
  const [sheetsBusy, setSheetsBusy] = useState(false);
  const [sheetsMessage, setSheetsMessage] = useState(null);   // { tone, text }
  const [sheetsPreview, setSheetsPreview] = useState(null);
  const [sheetsMode, setSheetsMode] = useState('append');
  const [showCustomerSheets, setShowCustomerSheets] = useState(false);
  const [editorSheet, setEditorSheet] = useState(null);
  const [showFullSheet, setShowFullSheet] = useState(false);
  const [showPurchaseTracker, setShowPurchaseTracker] = useState(false);
  const [showHangList, setShowHangList] = useState(false);
  // Categories are derived dynamically from the project's actual FF&E
  // categories (see the project useEffect below) so every real category is
  // listed and selectable — nothing depends on a hardcoded vocabulary.
  const [customerSheetCategories, setCustomerSheetCategories] = useState({});
  const [customerSheetRooms, setCustomerSheetRooms] = useState({});
  // FF&E transfer (export a sheet to a file, load it into another project)
  const [ffeBusy, setFfeBusy] = useState(false);
  const [ffeMessage, setFfeMessage] = useState(null);   // { tone: 'ok'|'error', text }
  const [ffeImportMode, setFfeImportMode] = useState('append');
  const ffeFileInputRef = React.useRef(null);
  // Bulk "Build all trade sheets" — stamp saved templates onto every sheet at once.
  const [showBuildAll, setShowBuildAll] = useState(false);
  const [buildSheets, setBuildSheets] = useState([]);      // [{sheet_key,label,templates}]
  const [buildSelections, setBuildSelections] = useState({}); // { sheet_key: template_id }
  const [buildBusy, setBuildBusy] = useState(false);
  const [buildResult, setBuildResult] = useState(null);

  const backendUrl = () => (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin);

  const openBuildAll = async () => {
    setShowBuildAll(true); setBuildResult(null);
    try {
      const res = await fetch(`${backendUrl()}/api/projects/${projectId}/sheet-templates-overview`);
      const d = await res.json();
      const sheets = d.sheets || [];
      setBuildSheets(sheets);
      const sel = {};
      sheets.forEach(s => {
        const tpls = s.templates || [];
        if (tpls.length) {
          const def = tpls.find(t => t.is_default);
          sel[s.sheet_key] = (def || tpls[0]).id;
        }
      });
      setBuildSelections(sel);
    } catch { setBuildSheets([]); }
  };

  const runBuildAll = async () => {
    setBuildBusy(true); setBuildResult(null);
    try {
      const res = await fetch(`${backendUrl()}/api/projects/${projectId}/build-all-sheets`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ selections: buildSelections }),
      });
      const d = await res.json();
      setBuildResult(d.results || {});
    } catch { setBuildResult({ error: 'failed' }); }
    finally { setBuildBusy(false); }
  };

  const handleFfeExport = async () => {
    setFfeBusy(true);
    setFfeMessage(null);
    try {
      const res = await fetch(`${backendUrl()}/api/projects/${projectId}/ffe-export`);
      if (!res.ok) throw new Error(`Export failed (${res.status})`);

      // Prefer the filename the server chose.
      const disposition = res.headers.get('content-disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `${project?.name || 'project'}_FFE.json`;

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setFfeMessage({ tone: 'ok', text: `Exported ${filename}` });
    } catch (err) {
      console.error('FF&E export failed:', err);
      setFfeMessage({ tone: 'error', text: err.message || 'Export failed' });
    } finally {
      setFfeBusy(false);
    }
  };

  const handleFfeImportFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';           // so picking the same file twice still fires
    if (!file) return;

    setFfeBusy(true);
    setFfeMessage(null);
    try {
      let payload;
      try {
        payload = JSON.parse(await file.text());
      } catch (e) {
        throw new Error('That file is not valid JSON. Pick an FF&E export from this app.');
      }

      const res = await fetch(`${backendUrl()}/api/projects/${projectId}/ffe-import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payload, mode: ffeImportMode }),
      });

      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || `Import failed (${res.status})`);

      setFfeMessage({
        tone: 'ok',
        text: `Imported ${body.rooms_imported} room${body.rooms_imported === 1 ? '' : 's'} and ` +
              `${body.items_imported} item${body.items_imported === 1 ? '' : 's'}` +
              (body.rooms_replaced ? ` (replaced ${body.rooms_replaced})` : '') +
              (body.source_project ? ` from "${body.source_project}"` : '') + '.',
      });
      await loadProjectData();
    } catch (err) {
      console.error('FF&E import failed:', err);
      setFfeMessage({ tone: 'error', text: err.message || 'Import failed' });
    } finally {
      setFfeBusy(false);
    }
  };

  useEffect(() => {
    loadProjectData();
    checkGoogleCalendarStatus();
  }, [projectId]);

  useEffect(() => {
    // Initialize room + category selections from the project's live FF&E data
    if (project && project.rooms) {
      const roomSelections = {};
      const categorySelections = {};
      project.rooms.forEach(room => {
        roomSelections[room.name || room.id] = true;
        (room.categories || []).forEach(cat => {
          const catName = (cat.name || '').trim() || 'Uncategorized';
          categorySelections[catName] = true;
        });
      });
      setCustomerSheetRooms(roomSelections);
      setCustomerSheetCategories(categorySelections);
    }
  }, [project]);

  // Check Google Calendar connection status
  const checkGoogleCalendarStatus = async () => {
    try {
      const BACKEND_URL = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin);
      const response = await fetch(`${BACKEND_URL}/api/auth/google/status`);
      if (response.ok) {
        const data = await response.json();
        setGoogleCalendarConnected(data.connected);
        if (data.email) setGoogleCalendarEmail(data.email);
      }
    } catch (error) {
      console.error('Error checking Google Calendar status:', error);
    }
  };

  const loadProjectData = async () => {
    try {
      const BACKEND_URL = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin);
      const response = await fetch(`${BACKEND_URL}/api/projects/${projectId}?sheet_type=ffe`);
      if (response.ok) {
        setProject(await response.json());
      }
    } catch (error) {
      console.error('Error loading project:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateElectricianSheet = async () => {
    try {
      const BACKEND_URL = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin);
      const response = await fetch(`${BACKEND_URL}/api/exports/${projectId}/electrician-sheet`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        alert('✅ Electrician sheet generated!');
      }
    } catch (error) {
      console.error('Error generating electrician sheet:', error);
      alert('Failed to generate electrician sheet');
    }
  };

  const generateLoadInSheets = async () => {
    try {
      const BACKEND_URL = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin);
      const response = await fetch(`${BACKEND_URL}/api/exports/${projectId}/load-in-sheets`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        alert('✅ Load-in room sheets generated!');
      }
    } catch (error) {
      console.error('Error generating load-in sheets:', error);
      alert('Failed to generate load-in sheets');
    }
  };

  const generateMoversFFE = async () => {
    try {
      const BACKEND_URL = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin);
      const response = await fetch(`${BACKEND_URL}/api/exports/${projectId}/movers-ffe`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        alert('✅ Mover\'s FFE generated!');
      }
    } catch (error) {
      console.error('Error generating mover\'s FFE:', error);
      alert('Failed to generate mover\'s FFE');
    }
  };

  const syncToGoogleCalendar = async () => {
    try {
      const BACKEND_URL = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin);
      
      if (!googleCalendarConnected) {
        // Need to connect first - initiate OAuth flow
        const response = await fetch(`${BACKEND_URL}/api/auth/google/login`);
        if (response.ok) {
          const data = await response.json();
          if (data.authorization_url) {
            window.location.href = data.authorization_url;
            return;
          }
        }
        alert('Failed to initiate Google Calendar connection');
        return;
      }
      
      // Already connected - sync the project
      const response = await fetch(`${BACKEND_URL}/api/calendar/google/sync/${projectId}`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.requires_auth) {
          // Token expired, need to reconnect
          const authResponse = await fetch(`${BACKEND_URL}/api/auth/google/login`);
          if (authResponse.ok) {
            const authData = await authResponse.json();
            if (authData.authorization_url) {
              window.location.href = authData.authorization_url;
              return;
            }
          }
        }
        alert(`✅ ${data.message || 'Synced to Google Calendar!'}`);
      } else {
        alert('Failed to sync to Google Calendar');
      }
    } catch (error) {
      console.error('Error syncing to Google Calendar:', error);
      alert('Failed to sync to Google Calendar');
    }
  };

  const syncToTeamsCalendar = async () => {
    // Report what the server actually says. This used to announce a successful
    // sync regardless of the response, so a failed or unimplemented sync looked
    // like it had worked.
    try {
      const BACKEND_URL = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin);
      const res = await fetch(`${BACKEND_URL}/api/calendar/teams/sync/${projectId}`, {
        method: 'POST'
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        alert('✅ Synced to Teams Calendar!');
        setTeamsCalendarConnected(true);
      } else {
        alert(`⚠️ Not synced.\n\n${data.message || `The server returned ${res.status}.`}`);
      }
    } catch (error) {
      console.error('Error syncing to Teams Calendar:', error);
      alert('Failed to reach the server to sync Teams Calendar');
    }
  };

  /** Read the sheet and report what WOULD be imported, without writing. */
  const checkGoogleSheet = async () => {
    if (!googleSheetsUrl.trim()) {
      setSheetsMessage({ tone: 'error', text: 'Paste the Google Sheets link first.' });
      return;
    }
    setSheetsBusy(true);
    setSheetsMessage(null);
    setSheetsPreview(null);
    try {
      const BACKEND_URL = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin);
      const res = await fetch(`${BACKEND_URL}/api/imports/google-sheets/${projectId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sheets_url: googleSheetsUrl, preview: true }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : `Could not read the sheet (${res.status})`);
      setSheetsPreview(data);
    } catch (e) {
      setSheetsMessage({ tone: 'error', text: e.message });
    } finally {
      setSheetsBusy(false);
    }
  };

  const importFromGoogleSheets = async () => {
    // Report the real outcome. This used to announce success regardless of the
    // response, against an endpoint that imported nothing at all.
    if (!googleSheetsUrl.trim()) {
      setSheetsMessage({ tone: 'error', text: 'Paste the Google Sheets link first.' });
      return;
    }
    setSheetsBusy(true);
    setSheetsMessage(null);
    try {
      const BACKEND_URL = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin);
      const res = await fetch(`${BACKEND_URL}/api/imports/google-sheets/${projectId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sheets_url: googleSheetsUrl, mode: sheetsMode }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : `Import failed (${res.status})`);
      setSheetsMessage({
        tone: 'ok',
        text: `Imported ${data.items} item(s) into ${data.rooms} room(s)` +
          (data.rows_skipped ? `, skipping ${data.rows_skipped} row(s) with no item name` : '') +
          `. Reload the FF&E tab to see them.`,
      });
      setSheetsPreview(null);
      setGoogleSheetsUrl('');
    } catch (e) {
      setSheetsMessage({ tone: 'error', text: e.message });
    } finally {
      setSheetsBusy(false);
    }
  };

  const generateCustomerSheets = async () => {
    try {
      const BACKEND_URL = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin);
      
      // Get selected categories and rooms
      const selectedCategories = Object.entries(customerSheetCategories)
        .filter(([_, selected]) => selected)
        .map(([category]) => category);
      
      const selectedRooms = Object.entries(customerSheetRooms)
        .filter(([_, selected]) => selected)
        .map(([room]) => room);
      
      const response = await fetch(`${BACKEND_URL}/api/exports/${projectId}/customer-sheets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          categories: selectedCategories,
          rooms: selectedRooms
        })
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        alert('✅ Customer sheets generated!');
        setShowCustomerSheets(false);
      } else {
        alert('Failed to generate customer sheets');
      }
    } catch (error) {
      console.error('Error generating customer sheets:', error);
      alert('Failed to generate customer sheets');
    }
  };

  const toggleAllCategories = (checked) => {
    const newCategories = {};
    Object.keys(customerSheetCategories).forEach(cat => {
      newCategories[cat] = checked;
    });
    setCustomerSheetCategories(newCategories);
  };

  const toggleAllRooms = (checked) => {
    const newRooms = {};
    Object.keys(customerSheetRooms).forEach(room => {
      newRooms[room] = checked;
    });
    setCustomerSheetRooms(newRooms);
  };

  if (loading) {
    return <div className="text-center py-12 text-[#D4C5A9]">Loading exports...</div>;
  }

  return (
    <div className="w-full" style={{ backgroundColor: '#0F172A', padding: '24px' }}>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2 className="text-3xl font-bold text-[#D4A574]">📄 Trade Sheets</h2>
        <button data-testid="build-all-sheets-btn" onClick={openBuildAll}
          className="px-4 py-2 bg-gradient-to-r from-[#D4A574] to-[#b8863f] hover:from-[#E2B989] hover:to-[#c9974a] text-black rounded-lg text-sm font-bold shadow">
          ⚡ Build All Sheets
        </button>
      </div>

      {/* FULL SHEET HEADER IMAGES — floor plan + examples printed atop every sheet */}
      <SheetHeaderImages projectId={projectId} />

      {/* FULL SHEET — several sheets bound into one document */}
      <div className="rounded-2xl p-6 border border-[#D4A574]/60 mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4" style={{
        background: 'linear-gradient(135deg, rgba(212,165,116,0.18) 0%, rgba(0,0,0,0.9) 50%, rgba(212,165,116,0.18) 100%)'
      }}>
        <div className="flex items-start gap-4">
          <div className="text-5xl">📚</div>
          <div>
            <h3 className="text-2xl font-bold text-[#D4A574]">FULL SHEET</h3>
            <p className="text-sm text-[#B49B7E] max-w-xl">
              Choose which sheets go in, and scope them all to the rooms and categories you want.
              Prints as one document with a contents page — each sheet keeps its own branded layout,
              and the room plan and finishes can ride along as a visual reference.
            </p>
          </div>
        </div>
        <button
          data-testid="open-full-sheet-btn"
          onClick={() => setShowFullSheet(true)}
          className="whitespace-nowrap bg-[#D4A574] hover:bg-[#E2B989] text-black px-6 py-3 rounded-lg font-bold flex items-center justify-center gap-2"
        >
          <Printer className="w-5 h-5" />
          Build Full Sheet
        </button>
      </div>

      {/* SPEC SHEETS */}
      <div className="rounded-2xl p-6 border border-[#D4A574]/60 mb-8" style={{
        background: 'linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(30,30,30,0.9) 50%, rgba(0,0,0,0.95) 100%)'
      }}>
        <h3 className="text-2xl font-bold text-[#D4A574] mb-6">📋 Spec Sheets & Install Guides</h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Electrician Sheet */}
          <div className="rounded-lg border border-[#D4A574]/50 p-6" style={{
            background: 'linear-gradient(135deg, rgba(139,69,19,0.2) 0%, rgba(0,0,0,0.9) 50%, rgba(139,69,19,0.2) 100%)'
          }}>
            <div className="text-center mb-4">
              <div className="text-5xl mb-3">⚡</div>
              <h4 className="text-xl font-bold text-[#D4A574] mb-2">Electrician Sheet</h4>
              <p className="text-sm text-[#B49B7E]">Installed lighting only (portable lamps excluded) — specs, photos, install notes</p>
            </div>
            <button
              data-testid="export-sheet-electrician-btn"
              onClick={() => setEditorSheet({ key: 'electrician', label: 'Electrician Sheet' })}
              className="w-full bg-[#8B4513] hover:bg-[#A0522D] text-white px-6 py-3 rounded-lg font-bold flex items-center justify-center gap-2"
            >
              <Printer className="w-5 h-5" />
              Open Editable Sheet
            </button>
          </div>

          {/* Plumber Sheet */}
          <div className="rounded-lg border border-[#D4A574]/50 p-6" style={{
            background: 'linear-gradient(135deg, rgba(37,99,235,0.2) 0%, rgba(0,0,0,0.9) 50%, rgba(37,99,235,0.2) 100%)'
          }}>
            <div className="text-center mb-4">
              <div className="text-5xl mb-3">🚰</div>
              <h4 className="text-xl font-bold text-[#D4A574] mb-2">Plumber Sheet</h4>
              <p className="text-sm text-[#B49B7E]">Faucets, sinks, tubs, showers &amp; valves with specs and rough-in notes</p>
            </div>
            <button
              data-testid="export-sheet-plumber-btn"
              onClick={() => setEditorSheet({ key: 'plumber', label: 'Plumber Sheet' })}
              className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-6 py-3 rounded-lg font-bold flex items-center justify-center gap-2"
            >
              <Printer className="w-5 h-5" />
              Open Editable Sheet
            </button>
          </div>

          {/* Tile Sheet */}
          <div className="rounded-lg border border-[#D4A574]/50 p-6" style={{
            background: 'linear-gradient(135deg, rgba(13,148,136,0.2) 0%, rgba(0,0,0,0.9) 50%, rgba(13,148,136,0.2) 100%)'
          }}>
            <div className="text-center mb-4">
              <div className="text-5xl mb-3">🧱</div>
              <h4 className="text-xl font-bold text-[#D4A574] mb-2">Tile Sheet</h4>
              <p className="text-sm text-[#B49B7E]">Tile, mosaic, slab &amp; stone with sizes, patterns and setting notes</p>
            </div>
            <button
              data-testid="export-sheet-tile-btn"
              onClick={() => setEditorSheet({ key: 'tile', label: 'Tile Sheet' })}
              className="w-full bg-[#0D9488] hover:bg-[#0F766E] text-white px-6 py-3 rounded-lg font-bold flex items-center justify-center gap-2"
            >
              <Printer className="w-5 h-5" />
              Open Editable Sheet
            </button>
          </div>

          {/* Wallpaper Sheet */}
          <div className="rounded-lg border border-[#D4A574]/50 p-6" style={{
            background: 'linear-gradient(135deg, rgba(180,83,9,0.2) 0%, rgba(0,0,0,0.9) 50%, rgba(180,83,9,0.2) 100%)'
          }}>
            <div className="text-center mb-4">
              <div className="text-5xl mb-3">🎨</div>
              <h4 className="text-xl font-bold text-[#D4A574] mb-2">Wallpaper Sheet</h4>
              <p className="text-sm text-[#B49B7E]">Wallcoverings &amp; grasscloth with roll counts and hanging notes</p>
            </div>
            <button
              data-testid="export-sheet-wallpaper-btn"
              onClick={() => setEditorSheet({ key: 'wallpaper', label: 'Wallpaper Sheet' })}
              className="w-full bg-[#B45309] hover:bg-[#92400E] text-white px-6 py-3 rounded-lg font-bold flex items-center justify-center gap-2"
            >
              <Printer className="w-5 h-5" />
              Open Editable Sheet
            </button>
            <button
              data-testid="wallpaper-hang-list-btn"
              onClick={() => setShowHangList(true)}
              className="mt-2 w-full border border-[#D4A574]/60 text-[#D4A574] hover:bg-[#D4A574] hover:text-black px-6 py-2 rounded-lg font-bold flex items-center justify-center gap-2 transition-colors"
            >
              📐 Wall-by-Wall Hang List
            </button>
          </div>

          {/* Art Hanging Sheet */}
          <div className="rounded-lg border border-[#D4A574]/50 p-6" style={{
            background: 'linear-gradient(135deg, rgba(79,70,229,0.2) 0%, rgba(0,0,0,0.9) 50%, rgba(79,70,229,0.2) 100%)'
          }}>
            <div className="text-center mb-4">
              <div className="text-5xl mb-3">🖼️</div>
              <h4 className="text-xl font-bold text-[#D4A574] mb-2">Art Hanging Sheet</h4>
              <p className="text-sm text-[#B49B7E]">Art, mirrors &amp; framed pieces with sizes and hanging-height notes</p>
            </div>
            <button
              data-testid="export-sheet-art-hanging-btn"
              onClick={() => setEditorSheet({ key: 'art-hanging', label: 'Art Hanging Sheet' })}
              className="w-full bg-[#4F46E5] hover:bg-[#4338CA] text-white px-6 py-3 rounded-lg font-bold flex items-center justify-center gap-2"
            >
              <Printer className="w-5 h-5" />
              Open Editable Sheet
            </button>
          </div>

          {/* Painters Sheet — requested 25 Aug */}
          <div className="rounded-lg border border-[#D4A574]/50 p-6" style={{
            background: 'linear-gradient(135deg, rgba(139,90,43,0.2) 0%, rgba(0,0,0,0.9) 50%, rgba(139,90,43,0.2) 100%)'
          }}>
            <div className="text-center mb-4">
              <div className="text-5xl mb-3">🖌️</div>
              <h4 className="text-xl font-bold text-[#D4A574] mb-2">Painters Sheet</h4>
              <p className="text-sm text-[#B49B7E]">Paint, stain and the surfaces they go on, by room — with colour and a column for finish/sheen</p>
            </div>
            <button
              data-testid="export-sheet-painter-btn"
              onClick={() => setEditorSheet({ key: 'painter', label: 'Painters Sheet' })}
              className="w-full bg-[#8B5A2B] hover:bg-[#7A4E24] text-white px-6 py-3 rounded-lg font-bold flex items-center justify-center gap-2"
            >
              <Printer className="w-5 h-5" />
              Open Editable Sheet
            </button>
          </div>

          {/* Cabinets Sheet — requested 25 Aug */}
          <div className="rounded-lg border border-[#D4A574]/50 p-6" style={{
            background: 'linear-gradient(135deg, rgba(120,80,40,0.2) 0%, rgba(0,0,0,0.9) 50%, rgba(120,80,40,0.2) 100%)'
          }}>
            <div className="text-center mb-4">
              <div className="text-5xl mb-3">🚪</div>
              <h4 className="text-xl font-bold text-[#D4A574] mb-2">Cabinets Sheet</h4>
              <p className="text-sm text-[#B49B7E]">The whole cabinet package by room — doors, trim, hardware, organizers and cabinet paint, plus the appliances and plumbing built into the run</p>
            </div>
            <button
              data-testid="export-sheet-cabinets-btn"
              onClick={() => setEditorSheet({ key: 'cabinets', label: 'Cabinets Sheet' })}
              className="w-full bg-[#785028] hover:bg-[#674423] text-white px-6 py-3 rounded-lg font-bold flex items-center justify-center gap-2"
            >
              <Printer className="w-5 h-5" />
              Open Editable Sheet
            </button>
          </div>

          {/* Purchase Tracker — requested 25 Aug */}
          <div className="rounded-lg border border-[#D4A574]/50 p-6" style={{
            background: 'linear-gradient(135deg, rgba(46,107,80,0.2) 0%, rgba(0,0,0,0.9) 50%, rgba(46,107,80,0.2) 100%)'
          }}>
            <div className="text-center mb-4">
              <div className="text-5xl mb-3">📦</div>
              <h4 className="text-xl font-bold text-[#D4A574] mb-2">Purchase Tracker</h4>
              <p className="text-sm text-[#B49B7E]">Everything on order, read across: cost, order number and date, expected and actual delivery, carrier and tracking, ship-to and status — totalled by room</p>
            </div>
            <button
              data-testid="export-sheet-purchase-tracker-btn"
              onClick={() => setShowPurchaseTracker(true)}
              className="w-full bg-[#2E6B50] hover:bg-[#255A43] text-white px-6 py-3 rounded-lg font-bold flex items-center justify-center gap-2"
            >
              <Printer className="w-5 h-5" />
              Open Purchase Tracker
            </button>
          </div>

          {/* Load-In Room Sheets */}
          <div className="rounded-lg border border-[#D4A574]/50 p-6" style={{
            background: 'linear-gradient(135deg, rgba(107,70,193,0.2) 0%, rgba(0,0,0,0.9) 50%, rgba(107,70,193,0.2) 100%)'
          }}>
            <div className="text-center mb-4">
              <div className="text-5xl mb-3">📦</div>
              <h4 className="text-xl font-bold text-[#D4A574] mb-2">Load-In Room Sheets</h4>
              <p className="text-sm text-[#B49B7E]">Big photos by room &mdash; 4 per page by default &mdash; plus floor plans and example pictures you attach</p>
            </div>
            <button
              data-testid="export-sheet-load-in-btn"
              onClick={() => setEditorSheet({ key: 'load-in', label: 'Load-In Sheets' })}
              className="w-full bg-[#6B46C1] hover:bg-[#7B56D1] text-white px-6 py-3 rounded-lg font-bold flex items-center justify-center gap-2"
            >
              <Printer className="w-5 h-5" />
              Open Editable Sheet
            </button>
          </div>

          {/* Mover's Simplified FFE */}
          <div className="rounded-lg border border-[#D4A574]/50 p-6" style={{
            background: 'linear-gradient(135deg, rgba(16,185,129,0.2) 0%, rgba(0,0,0,0.9) 50%, rgba(16,185,129,0.2) 100%)'
          }}>
            <div className="text-center mb-4">
              <div className="text-5xl mb-3">🚚</div>
              <h4 className="text-xl font-bold text-[#D4A574] mb-2">Mover&rsquo;s FFE Sheet</h4>
              <p className="text-sm text-[#B49B7E]">Simplified spreadsheet (no pricing) - just item, room, quantity</p>
            </div>
            <button
              data-testid="export-sheet-movers-btn"
              onClick={() => setEditorSheet({ key: 'movers', label: "Mover's FFE" })}
              className="w-full bg-[#10B981] hover:bg-[#059669] text-white px-6 py-3 rounded-lg font-bold flex items-center justify-center gap-2"
            >
              <Printer className="w-5 h-5" />
              Open Editable Sheet
            </button>
          </div>

          {/* Customer Sheets */}
          <div className="rounded-lg border border-[#D4A574]/50 p-6" style={{
            background: 'linear-gradient(135deg, rgba(244,114,182,0.2) 0%, rgba(0,0,0,0.9) 50%, rgba(244,114,182,0.2) 100%)'
          }}>
            <div className="text-center mb-4">
              <div className="text-5xl mb-3">📋</div>
              <h4 className="text-xl font-bold text-[#D4A574] mb-2">Customer Sheets</h4>
              <p className="text-sm text-[#B49B7E]">Select categories to print for client close & install</p>
            </div>
            <button
              onClick={() => setShowCustomerSheets(true)}
              className="w-full bg-[#F472B6] hover:bg-[#EC4899] text-white px-6 py-3 rounded-lg font-bold flex items-center justify-center gap-2"
            >
              <CheckSquare className="w-5 h-5" />
              Select & Print
            </button>
          </div>

          {/* Editable Customer Sheet (persisted, floor-grouped, add/edit items) */}
          <div className="rounded-lg border border-[#D4A574]/50 p-6" style={{
            background: 'linear-gradient(135deg, rgba(16,185,129,0.2) 0%, rgba(0,0,0,0.9) 50%, rgba(16,185,129,0.2) 100%)'
          }}>
            <div className="text-center mb-4">
              <div className="text-5xl mb-3">📝</div>
              <h4 className="text-xl font-bold text-[#D4A574] mb-2">Editable Customer Sheet</h4>
              <p className="text-sm text-[#B49B7E]">Edit, add/remove items &amp; notes, grouped by floor — saved &amp; printable</p>
            </div>
            <button
              data-testid="export-editable-sheet-btn"
              onClick={() => setEditorSheet({ key: 'customer', label: 'Customer Sheet' })}
              className="w-full bg-[#10B981] hover:bg-[#059669] text-white px-6 py-3 rounded-lg font-bold flex items-center justify-center gap-2"
            >
              <FileText className="w-5 h-5" />
              Open Editable Sheet
            </button>
          </div>
        </div>
      </div>

      {/* CALENDAR SYNC */}
      <div className="rounded-2xl p-6 border border-[#D4A574]/60 mb-8" style={{
        background: 'linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(30,30,30,0.9) 50%, rgba(0,0,0,0.95) 100%)'
      }}>
        <h3 className="text-2xl font-bold text-[#D4A574] mb-6">📥 Import & Sync</h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* FF&E Transfer - export a sheet to a file and load it into another project */}
          <div className="rounded-lg border border-[#D4A574]/50 p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="text-4xl">🔄</div>
              <div>
                <h4 className="text-xl font-bold text-[#D4C5A9]">FF&amp;E Transfer</h4>
                <p className="text-sm text-[#B49B7E]">Copy a full FF&amp;E to another project</p>
              </div>
            </div>
            <ul className="text-sm text-gray-300 mb-4 space-y-1">
              <li>• Rooms, categories &amp; every item</li>
              <li>• Keeps floors and ordering</li>
              <li>• Export here, import on the other project</li>
            </ul>

            <button
              data-testid="ffe-export-btn"
              onClick={handleFfeExport}
              disabled={ffeBusy}
              className="w-full bg-[#D4A574] hover:bg-[#B49B7E] disabled:opacity-50 text-black px-6 py-3 rounded-lg font-bold mb-3"
            >
              {ffeBusy ? 'Working…' : 'Export FF&E to File'}
            </button>

            <div className="flex items-center gap-2 mb-2 text-sm text-[#D4C5A9]">
              <label htmlFor="ffe-import-mode" className="shrink-0">On import:</label>
              <select
                id="ffe-import-mode"
                data-testid="ffe-import-mode"
                value={ffeImportMode}
                onChange={(e) => setFfeImportMode(e.target.value)}
                className="flex-1 bg-black/60 border border-[#D4A574]/60 rounded px-2 py-1 text-[#D4C5A9]"
              >
                <option value="append">Add to this project</option>
                <option value="replace">Replace this project&apos;s FF&amp;E</option>
              </select>
            </div>

            <input
              ref={ffeFileInputRef}
              type="file"
              accept="application/json,.json"
              onChange={handleFfeImportFile}
              className="hidden"
              data-testid="ffe-import-input"
            />
            <button
              data-testid="ffe-import-btn"
              onClick={() => ffeFileInputRef.current?.click()}
              disabled={ffeBusy}
              className="w-full bg-[#3B82F6] hover:bg-[#2563EB] disabled:opacity-50 text-white px-6 py-3 rounded-lg font-bold"
            >
              {ffeBusy ? 'Working…' : 'Import FF&E from File'}
            </button>

            {ffeMessage && (
              <p
                data-testid="ffe-transfer-message"
                className={`mt-3 text-sm ${ffeMessage.tone === 'ok' ? 'text-green-400' : 'text-red-400'}`}
              >
                {ffeMessage.text}
              </p>
            )}
          </div>

          {/* Google Sheets Import */}
          <div className="rounded-lg border border-[#D4A574]/50 p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="text-4xl">📊</div>
              <div>
                <h4 className="text-xl font-bold text-[#D4C5A9]">Google Sheets</h4>
                <p className="text-sm text-[#B49B7E]">Import FFE data</p>
              </div>
            </div>
            <ul className="text-sm text-gray-300 mb-4 space-y-1">
              <li>• Import items from spreadsheet</li>
              <li>• Auto-create rooms & categories</li>
              <li>• Bulk data entry</li>
            </ul>
            <button
              onClick={() => setShowGoogleSheetsImport(true)}
              className="w-full bg-[#34A853] hover:bg-[#2D8E47] text-white px-6 py-3 rounded-lg font-bold"
            >
              Import from Google Sheets
            </button>
          </div>

          {/* Google Calendar */}
          <div className="rounded-lg border border-[#D4A574]/50 p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="text-4xl">📅</div>
              <div>
                <h4 className="text-xl font-bold text-[#D4C5A9]">Google Calendar</h4>
                <p className="text-sm text-[#B49B7E]">
                  {googleCalendarConnected ? `Connected ✓ ${googleCalendarEmail ? `(${googleCalendarEmail})` : ''}` : 'Not connected'}
                </p>
              </div>
            </div>
            <ul className="text-sm text-gray-300 mb-4 space-y-1">
              <li>• Auto-create delivery date events</li>
              <li>• Sync installation dates</li>
              <li>• Sync project completion dates</li>
              <li>• Reminder notifications</li>
            </ul>
            <button
              onClick={syncToGoogleCalendar}
              className="w-full bg-[#4285F4] hover:bg-[#3367D6] text-white px-6 py-3 rounded-lg font-bold"
            >
              {googleCalendarConnected ? 'Sync Project Dates' : 'Connect Google Calendar'}
            </button>
          </div>

          {/* Teams Calendar */}
          <div className="rounded-lg border border-[#D4A574]/50 p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="text-4xl">📅</div>
              <div>
                <h4 className="text-xl font-bold text-[#D4C5A9]">Microsoft Teams Calendar</h4>
                <p className="text-sm text-[#B49B7E]">
                  {teamsCalendarConnected ? 'Connected ✓' : 'Not connected'}
                </p>
              </div>
            </div>
            <ul className="text-sm text-gray-300 mb-4 space-y-1">
              <li>• Sync with team calendars</li>
              <li>• Installation scheduling</li>
              <li>• Team availability tracking</li>
              <li>• Meeting coordination</li>
            </ul>
            <button
              onClick={syncToTeamsCalendar}
              className="w-full bg-[#6264A7] hover:bg-[#464775] text-white px-6 py-3 rounded-lg font-bold"
            >
              {teamsCalendarConnected ? 'Re-Sync' : 'Connect'} Teams Calendar
            </button>
          </div>
        </div>
      </div>

      {/* GOOGLE SHEETS IMPORT MODAL */}
      {showGoogleSheetsImport && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-[#1E293B] border-2 border-[#D4A574] rounded-2xl p-8 max-w-md w-full">
            <h3 className="text-2xl font-bold text-[#D4A574] mb-6">📊 Import from Google Sheets</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-[#B49B7E] mb-2">Google Sheets URL</label>
                <input
                  type="text"
                  value={googleSheetsUrl}
                  onChange={(e) => setGoogleSheetsUrl(e.target.value)}
                  className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-[#D4A574]"
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                />
                <div className="text-xs text-gray-400 mt-2">
                  In Google Sheets: Share &rarr; General access &rarr; &ldquo;Anyone with the link&rdquo; (Viewer is enough).
                </div>
              </div>

              <div className="bg-blue-900/20 border border-blue-500/50 p-4 rounded-lg">
                <div className="text-sm text-blue-300 mb-2"><strong>Columns</strong> (a header row is required)</div>
                <div className="text-xs text-gray-300 space-y-1">
                  <p>Required: <span className="text-[#D4A574]">Item</span> (or Name / Description / Product)</p>
                  <p>Used if present: Floor, Room, Category, Sub Category, Vendor, SKU, Size, Finish, Qty, Status, Notes, Link, Image</p>
                  <p className="text-gray-400">Order does not matter, and extra columns are ignored.</p>
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-[#D4C5A9]">
                Existing FF&amp;E:
                <select value={sheetsMode} onChange={e => setSheetsMode(e.target.value)}
                  data-testid="sheets-mode"
                  className="bg-gray-800 text-white px-2 py-1 rounded border border-[#D4A574]/40 text-sm">
                  <option value="append">Keep it, add these rooms</option>
                  <option value="replace">Replace the FF&amp;E with this sheet</option>
                </select>
              </label>

              {sheetsMessage && (
                <p className={`text-sm ${sheetsMessage.tone === 'ok' ? 'text-emerald-400' : 'text-red-400'}`}
                  data-testid="sheets-message">{sheetsMessage.text}</p>
              )}

              {sheetsPreview && (
                <div className="border border-[#D4A574]/40 rounded p-3 text-xs text-gray-200 max-h-56 overflow-y-auto"
                  data-testid="sheets-preview">
                  <p className="text-[#D4A574] font-bold mb-1">
                    {sheetsPreview.items} item(s) in {sheetsPreview.rooms} room(s) — nothing imported yet
                  </p>
                  <p className="text-gray-400 mb-2">
                    {sheetsPreview.rows_read} row(s) read
                    {sheetsPreview.rows_skipped ? `, ${sheetsPreview.rows_skipped} skipped (no item name)` : ''}
                    {sheetsPreview.columns_ignored?.length
                      ? ` · ignoring: ${sheetsPreview.columns_ignored.join(', ')}` : ''}
                  </p>
                  <ul className="space-y-1">
                    {(sheetsPreview.structure || []).map(r => (
                      <li key={r.room}>
                        <span className="text-white font-semibold">{r.room}</span>
                        {r.floor ? <span className="text-gray-400"> · {r.floor}</span> : null}
                        <span className="text-[#B49B7E]"> — {r.items} item(s): {r.categories.join(', ')}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={checkGoogleSheet}
                disabled={sheetsBusy}
                data-testid="sheets-check"
                className="flex-1 bg-[#3B82F6] hover:bg-[#2563EB] disabled:opacity-50 text-white px-4 py-3 rounded-lg font-bold"
              >
                {sheetsBusy ? 'Reading…' : '1. Check sheet'}
              </button>
              <button
                onClick={importFromGoogleSheets}
                disabled={sheetsBusy || !sheetsPreview}
                data-testid="sheets-import"
                title={sheetsPreview ? '' : 'Check the sheet first'}
                className="flex-1 bg-[#34A853] hover:bg-[#2D8E47] disabled:opacity-40 text-white px-4 py-3 rounded-lg font-bold"
              >
                2. Import
              </button>
              <button
                onClick={() => { setShowGoogleSheetsImport(false); setSheetsPreview(null); setSheetsMessage(null); }}
                className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-3 rounded-lg font-bold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CUSTOMER SHEETS MODAL */}
      {showCustomerSheets && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1E293B] border-2 border-[#D4A574] rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-[#D4A574] mb-6">📋 Customer Sheets - Select Items to Print</h3>
            <p className="text-[#B49B7E] mb-6">Choose which categories and rooms to include in the customer handoff document.</p>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Categories Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-xl font-bold text-[#D4C5A9]">📂 Categories</h4>
                  <div className="flex gap-2">
                    <button
                      onClick={() => toggleAllCategories(true)}
                      className="text-xs bg-green-700 hover:bg-green-600 text-white px-3 py-1 rounded"
                    >
                      Select All
                    </button>
                    <button
                      onClick={() => toggleAllCategories(false)}
                      className="text-xs bg-gray-600 hover:bg-gray-500 text-white px-3 py-1 rounded"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  {Object.keys(customerSheetCategories).length > 0 ? (
                    Object.keys(customerSheetCategories).sort((a, b) => a.localeCompare(b)).map((key) => (
                      <label key={key} className="flex items-center gap-2 p-2 rounded-lg bg-black/30 hover:bg-black/50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={customerSheetCategories[key] || false}
                          onChange={(e) => setCustomerSheetCategories(prev => ({
                            ...prev,
                            [key]: e.target.checked
                          }))}
                          className="w-4 h-4 text-[#D4A574] bg-gray-800 border-gray-600 rounded focus:ring-[#D4A574]"
                        />
                        <span className="text-sm text-[#D4C5A9]">📂 {key}</span>
                      </label>
                    ))
                  ) : (
                    <p className="text-gray-500 text-sm col-span-2">No categories found in this project&rsquo;s FF&amp;E.</p>
                  )}
                </div>
              </div>

              {/* Rooms Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-xl font-bold text-[#D4C5A9]">🏠 Rooms</h4>
                  <div className="flex gap-2">
                    <button
                      onClick={() => toggleAllRooms(true)}
                      className="text-xs bg-green-700 hover:bg-green-600 text-white px-3 py-1 rounded"
                    >
                      Select All
                    </button>
                    <button
                      onClick={() => toggleAllRooms(false)}
                      className="text-xs bg-gray-600 hover:bg-gray-500 text-white px-3 py-1 rounded"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {Object.keys(customerSheetRooms).length > 0 ? (
                    Object.entries(customerSheetRooms).map(([roomName, selected]) => (
                      <label key={roomName} className="flex items-center gap-2 p-3 rounded-lg bg-black/30 hover:bg-black/50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={(e) => setCustomerSheetRooms(prev => ({
                            ...prev,
                            [roomName]: e.target.checked
                          }))}
                          className="w-4 h-4 text-[#D4A574] bg-gray-800 border-gray-600 rounded focus:ring-[#D4A574]"
                        />
                        <span className="text-sm text-[#D4C5A9]">🚪 {roomName}</span>
                      </label>
                    ))
                  ) : (
                    <p className="text-gray-500 text-sm">No rooms available. Add rooms to your project first.</p>
                  )}
                </div>
              </div>
            </div>

            {/* Preview Info */}
            <div className="mt-6 p-4 bg-[#D4A574]/10 border border-[#D4A574]/30 rounded-lg">
              <h5 className="text-[#D4A574] font-bold mb-2">📄 Document Preview</h5>
              <p className="text-sm text-[#B49B7E]">
                Selected: {Object.values(customerSheetCategories).filter(Boolean).length} categories, {Object.values(customerSheetRooms).filter(Boolean).length} rooms
              </p>
              <p className="text-xs text-gray-400 mt-2">
                The customer sheet will include: item name, vendor, finish/color, quantity, and image for each selected item.
                This is designed to be given to clients at project close and install.
              </p>
            </div>
            
            <div className="flex gap-4 mt-6">
              <button
                onClick={generateCustomerSheets}
                className="flex-1 bg-[#F472B6] hover:bg-[#EC4899] text-white px-6 py-3 rounded-lg font-bold flex items-center justify-center gap-2"
              >
                <Printer className="w-5 h-5" />
                Generate Customer Sheets
              </button>
              <button
                onClick={() => setShowCustomerSheets(false)}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-bold"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDITABLE EXPORT SHEET */}
      {showFullSheet && (
        <FullSheetBuilder projectId={projectId} onClose={() => setShowFullSheet(false)} />
      )}

      {editorSheet && (
        <EditableExportSheet
          projectId={projectId}
          sheetKey={editorSheet.key}
          sheetLabel={editorSheet.label}
          onClose={() => setEditorSheet(null)}
        />
      )}

      {showPurchaseTracker && (
        <PurchaseTrackerModal projectId={projectId} onClose={() => setShowPurchaseTracker(false)} />
      )}
      {showHangList && (
        <WallpaperHangList projectId={projectId} projectName={project?.name || ''} onClose={() => setShowHangList(false)} />
      )}

      {showBuildAll && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-[60]">
          <div className="bg-[#1E293B] border-2 border-[#D4A574] rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="build-all-modal">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-bold text-[#D4A574]">⚡ Build all trade sheets</h3>
              <button data-testid="build-all-close" onClick={() => setShowBuildAll(false)} className="text-gray-400 hover:text-white">✕</button>
            </div>
            <p className="text-xs text-gray-400 mb-4">Pick a saved template for each trade and stamp them all onto this home in one click. Sheets with no template are skipped.</p>
            <div className="space-y-2 mb-4">
              {buildSheets.map((s) => (
                <div key={s.sheet_key} className="flex items-center gap-2 bg-slate-800 rounded px-3 py-2">
                  <span className="flex-1 text-sm text-[#D4C5A9]">{s.label}</span>
                  {(s.templates || []).length === 0 ? (
                    <span className="text-xs text-gray-500 italic">no template</span>
                  ) : (
                    <select data-testid={`build-select-${s.sheet_key}`} value={buildSelections[s.sheet_key] || ''}
                      onChange={(e) => setBuildSelections({ ...buildSelections, [s.sheet_key]: e.target.value })}
                      className="bg-slate-900 border border-slate-600 rounded text-white text-xs px-2 py-1 max-w-[220px]">
                      <option value="">— skip —</option>
                      {(s.templates || []).map((t) => <option key={t.id} value={t.id}>{t.is_default ? '★ ' : ''}{t.name}</option>)}
                    </select>
                  )}
                  {buildResult && buildResult[s.sheet_key] && (
                    buildResult[s.sheet_key].error
                      ? <span className="text-xs text-red-400">✕ {buildResult[s.sheet_key].error}</span>
                      : ((buildResult[s.sheet_key].included_wh || 0) + (buildResult[s.sheet_key].applied || 0) > 0
                          ? <span className="text-xs text-emerald-400">✓ {buildResult[s.sheet_key].included_wh || 0} src · {buildResult[s.sheet_key].applied || 0} filled</span>
                          : <span className="text-xs text-amber-400">✓ already set</span>)
                  )}
                </div>
              ))}
              {buildSheets.length === 0 && <p className="text-sm text-gray-500">Loading…</p>}
            </div>
            <div className="flex justify-end gap-2">
              <button data-testid="build-all-run" onClick={runBuildAll} disabled={buildBusy}
                className="px-4 py-2 bg-[#D4A574] hover:bg-[#E2B989] text-black rounded text-sm font-bold disabled:opacity-50">
                {buildBusy ? 'Building…' : buildResult ? '✓ Build again' : '⚡ Build all'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExportsDashboard;